import React, { FunctionComponent, useEffect, useMemo, useState } from 'react';
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useParams,
} from 'react-router-dom';
import { useAsyncCallback } from 'react-async-hook';
import { unwrapResult } from '@reduxjs/toolkit';
import moment from 'moment';
import { ROUTES } from '../../utils/routes';
import { DealerLaunchPageFrame } from './DealerLaunchPageFrame';
import { useAppDispatch, useTypedSelector } from '../../redux/store';
import { fetchDealer, updateDealer } from '../../redux/dealers/thunks';
import { Dealer } from '../../types/dealerTypes';
import { UpdateDealerRequest } from '../../services/DealerService';
import {
  DEALER_LAUNCH_COMPLETE_STEP,
  filterDealerLaunchSections,
  filterDealerLaunchSteps,
  getNextStep,
  isReadyToLaunch,
  isVerifiedInventory,
} from './steps';
import { Spinner } from '../Spinner/Spinner';
import { DealerLaunchStep, NextStepOptions, SaveDealerOptions } from './types';

type Props = {
  showAdminView?: boolean;
};

export const DealerLaunchDashboard: FunctionComponent<Props> = ({
  showAdminView,
}) => {
  const dispatch = useAppDispatch();
  const [obtainedDealerInfo, setObtainedDealerInfo] = useState(false);
  const history = useHistory();
  const dispatchUpdateDealer = useAsyncCallback((arg: UpdateDealerRequest) =>
    dispatch(updateDealer(arg)).then(unwrapResult),
  );
  const authStore = useTypedSelector((state) => state.auth);
  const dealerStore = useTypedSelector((state) => state.dealers);
  const { dealer } = dealerStore;

  const filteredDealerLaunchSteps = useMemo(
    () => filterDealerLaunchSteps(dealer),
    [dealer],
  );

  const filteredDealerLaunchSections = useMemo(
    () => filterDealerLaunchSections(dealer),
    [dealer],
  );

  // fetching the dealer ID that the admin wants to access from URL
  const { dealerId } = useParams<{ dealerId: string }>();
  let adminAccessDealerId: string | undefined | null;

  // differentiate whether the URL is from admin or dealer
  if (showAdminView) {
    // fetching the dealerID if it has been saved in the session storage
    const sessionDealerId = sessionStorage.getItem('adminAccessDealer');
    adminAccessDealerId = dealerId || sessionDealerId;

    // if the admin wants to access the dealer different from the one in the session storage, it needs to update with the new one
    if (sessionDealerId !== adminAccessDealerId) {
      sessionStorage.setItem('adminAccessDealer', dealerId);
      // reload in order to prevent from redirecting to the admin's home page.
      window.location.reload();
    }
  }

  // load the current user's dealer
  useEffect(() => {
    if (
      adminAccessDealerId ||
      (authStore.currentUser?.dealer_id &&
        authStore.currentUser?.dealer_id !== dealer?.id)
    ) {
      // if accessDealerID exists, we use it to fetch the data instead of using the dealer Id from authStore
      // typically only dealer (client) has dealer_id in the authStore Obj
      const accessDealerID =
        adminAccessDealerId || authStore.currentUser?.dealer_id;

      if (accessDealerID) {
        dispatch(fetchDealer(accessDealerID));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authStore.currentUser?.dealer_id]);

  useEffect(() => {
    if (obtainedDealerInfo || !dealer || !dealer.launchpad) return;
    // Trigger login dates code here
    setObtainedDealerInfo(true);
    const loginTime = moment().toString();
    const loginUpdate: Dealer['launchpad'] = {};
    loginUpdate.lastLogin = loginTime;
    if (!dealer.launchpad.firstLogin) {
      loginUpdate.firstLogin = loginTime;
    }
    dispatch(
      updateDealer({
        dealerId: dealer.id,
        dealerUpdate: { launchpad: loginUpdate },
        mergeObjectFields: true,
        allowLaunchpadAlerts: true,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealer, obtainedDealerInfo]);

  if (authStore.fetching || !dealer) return <Spinner variant="fullscreen" />;

  const prevStep = async () => {
    history.goBack();
  };
  const saveDealer = async (
    dealerUpdate?: Partial<Dealer>,
    options?: SaveDealerOptions,
  ) => {
    // only update the dealer if there's an change that needs to be saved
    if (!dealerUpdate) return dealer;

    // specifying mergeObjectFields will merge the dealer update with values from the original
    // dealer object for any json columns, this allows targeted updates within json objects to save
    // correctly as normally the backend will only merge columns, not objects within the columns
    return dispatchUpdateDealer.execute({
      dealerId: dealer.id,
      dealerUpdate,
      mergeObjectFields: !options?.disableDealerUpdateMerge,
      allowLaunchpadAlerts: true,
    });
  };
  const nextStep = async (
    index: number,
    dealerUpdate?: Partial<Dealer>,
    options?: NextStepOptions,
  ) => {
    const updatedDealer = await saveDealer(dealerUpdate, options).catch(
      () => null,
    );
    if (!updatedDealer) return;

    const route =
      getNextStep({
        currentIndex: index,
        dealer: updatedDealer,
        filteredSteps: filteredDealerLaunchSteps,
        options,
      })?.route || ROUTES.DEALER_DASHBOARD_REVIEW;

    history.push(route.getPath(dealer));
  };

  const skipStep = (index: number) => {
    const route =
      getNextStep({
        currentIndex: index,
        dealer,
        filteredSteps: filteredDealerLaunchSteps,
      })?.route || ROUTES.DEALER_DASHBOARD_REVIEW;
    history.push(route.getPath(dealer));
  };

  const renderStepRoute = (step: DealerLaunchStep, index: number) => (
    <Route key={step.route.template} path={step.route.template}>
      <DealerLaunchPageFrame
        step={step}
        dealer={dealer}
        error={dispatchUpdateDealer.error}
        skipStep={() => skipStep(index)}
        showAdminView={showAdminView}
      >
        <step.Component
          step={step}
          dealer={dealer}
          prevStep={prevStep}
          nextStep={(dealerUpdate, options) =>
            nextStep(index, dealerUpdate, options)
          }
          saveDealer={saveDealer}
          skipStep={() => skipStep(index)}
        />
      </DealerLaunchPageFrame>
    </Route>
  );

  // if the dealer is marked as ready to launch, only show the review page
  if (isReadyToLaunch(dealer)) {
    // index doesn't matter here as step navigation is disabled
    return (
      <>
        {renderStepRoute(DEALER_LAUNCH_COMPLETE_STEP, 0)}
        <Redirect push to={ROUTES.DEALER_DASHBOARD_REVIEW.getPath()} />
      </>
    );
  }

  // if the dealer has verified inventory, review summary screen is accessible through "Review" section
  // we hide the review button on the navigation sidebar, since it's already accessible in "Review" section
  const hideShowReview = isVerifiedInventory(dealer);

  return (
    <Switch>
      {filteredDealerLaunchSteps.map(renderStepRoute)}
      {filteredDealerLaunchSections.map((section, index) => (
        <Route key={section.route.template} path={section.route.template}>
          <DealerLaunchPageFrame
            section={section}
            dealer={dealer}
            error={dispatchUpdateDealer.error}
            skipStep={() => skipStep(index)}
            hideReviewNav={hideShowReview}
            showAdminView={showAdminView}
          >
            <section.Component section={section} dealer={dealer} />
          </DealerLaunchPageFrame>
        </Route>
      ))}
      <Redirect push to={ROUTES.DEALER_DASHBOARD_HOME.getPath()} />
    </Switch>
  );
};
